import React, { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useLang } from '../context/LangContext';
import { useAuth } from '../context/AuthContext';
import * as api from '../api';
import { useSEO } from '../hooks/useSEO';
import './Login.css';

// One page, three states: signing in, asking for a password back, and setting a
// new one from a link that arrived by mail.
//
// The middle state has two faces, and which one shows is the server's answer,
// not a build-time choice. Recovery used to mint a code and print it into the
// server log for staff to read out — a code that grants entry to any cabinet has
// no business sitting in a log file. So it now goes by mail or not at all, and
// where no mail provider is configured the page says plainly that the office
// issues passwords, rather than promising a letter that will never come.
const SIGN_IN = 'sign-in';
const FORGOT = 'forgot';
const RESET = 'reset';

export default function Login() {
  const { t, lang } = useLang();
  const { user, loading, signIn } = useAuth();
  const navigate = useNavigate();
  useSEO(t('cab_login_title'), t('cab_login_subtitle'));

  const [params] = useSearchParams();
  // A link from the mail lands here with the token in the query string. It is
  // read once, on the first render, so that clearing the field later does not
  // resurrect it from the URL.
  const [resetToken] = useState(() => params.get('reset') || '');
  const [mode, setMode] = useState(resetToken ? RESET : SIGN_IN);
  // Once the token is in hand the URL does not need it any more, and leaving
  // it there puts a working key into browser history and into whatever the
  // address bar gets shared with. Replacing rather than pushing keeps Back
  // going where the person expects.
  useEffect(() => {
    if (resetToken) navigate('/login', { replace: true });
  }, [resetToken, navigate]);

  // null while the answer is in flight: neither face of the forgot screen is
  // right to show before the server has said which one applies.
  const [selfServe, setSelfServe] = useState(null);
  // Holds an email or, for a parent, their phone number — the server accepts
  // either.
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  // A failure here is not worth an error on the sign-in screen: the fallback
  // is the office wording, which is true wherever mail is not configured.
  useEffect(() => {
    let live = true;
    api.getAuthConfig()
      .then((c) => { if (live) setSelfServe(Boolean(c.selfServeRecovery)); })
      .catch(() => { if (live) setSelfServe(false); });
    return () => { live = false; };
  }, []);

  if (loading) return <div className="login-page"><div className="login-card">{t('cab_loading')}</div></div>;
  if (user) return <Navigate to="/cabinet" replace />;

  const run = async (fn, after) => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await fn();
      if (after) after();
    } catch (e) {
      setError(e.message || 'Error');
    } finally {
      setBusy(false);
    }
  };

  const onSignIn = (e) => {
    e.preventDefault();
    run(() => signIn(email, password), () => navigate('/cabinet'));
  };

  const onForgot = (e) => {
    e.preventDefault();
    // The server answers the same way whether or not the address is known, so
    // the notice here has to be worded the same way too — otherwise the page
    // would leak what the server deliberately does not.
    run(() => api.forgotPassword(email, lang), () => setNotice(t('login_forgot_mailed')));
  };

  const onReset = (e) => {
    e.preventDefault();
    if (password !== password2) { setError(t('login_reset_mismatch')); return; }
    if (password.length < 8) { setError(t('login_reset_short')); return; }
    run(() => api.resetPassword(resetToken, password), () => {
      setMode(SIGN_IN);
      setPassword('');
      setPassword2('');
      setNotice(t('login_reset_done'));
    });
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-logo"><span>SkillUp</span></div>

        {api.isDemo() && <p className="login-error">Demo mode has no backend — signing in is disabled.</p>}
        {error && <p className="login-error">{error}</p>}
        {notice && <p className="login-notice">{notice}</p>}

        {mode === SIGN_IN && (
          <>
            <h2>{t('cab_login_title')}</h2>
            <p>{t('cab_login_subtitle')}</p>
            <form className="login-form" onSubmit={onSignIn}>
              <label>
                {t('login_identifier')}
                <input type="text" autoComplete="username" required
                       value={email} onChange={(e) => setEmail(e.target.value)} />
              </label>
              <p className="login-hint">{t('login_identifier_hint')}</p>
              <label>
                {t('login_password')}
                <input type="password" autoComplete="current-password" required
                       value={password} onChange={(e) => setPassword(e.target.value)} />
              </label>
              <button className="login-submit" type="submit" disabled={busy || api.isDemo()}>
                {busy ? t('cab_loading') : t('login_submit')}
              </button>
            </form>
            <button className="login-link" type="button" onClick={() => { setMode(FORGOT); setError(''); }}>
              {t('login_forgot')}
            </button>
          </>
        )}

        {mode === FORGOT && (
          <>
            <h2>{t('login_forgot')}</h2>
            {selfServe === null && <p>{t('cab_loading')}</p>}
            {selfServe === false && <p>{t('login_forgot_help')}</p>}
            {selfServe === true && (
              <>
                <p>{t('login_forgot_mail_help')}</p>
                <form className="login-form" onSubmit={onForgot}>
                  <label>
                    {t('login_email')}
                    <input type="email" autoComplete="email" required
                           value={email} onChange={(e) => setEmail(e.target.value)} />
                  </label>
                  <button className="login-submit" type="submit" disabled={busy || api.isDemo()}>
                    {busy ? t('cab_loading') : t('login_forgot_submit')}
                  </button>
                </form>
              </>
            )}
            <button className="login-link" type="button" onClick={() => setMode(SIGN_IN)}>{t('login_back')}</button>
          </>
        )}

        {mode === RESET && (
          <>
            <h2>{t('login_reset_title')}</h2>
            <p>{t('login_reset_help')}</p>
            <form className="login-form" onSubmit={onReset}>
              <label>
                {t('login_reset_password')}
                <input type="password" autoComplete="new-password" required minLength={8}
                       value={password} onChange={(e) => setPassword(e.target.value)} />
              </label>
              <label>
                {t('login_reset_repeat')}
                <input type="password" autoComplete="new-password" required minLength={8}
                       value={password2} onChange={(e) => setPassword2(e.target.value)} />
              </label>
              <button className="login-submit" type="submit" disabled={busy || api.isDemo()}>
                {busy ? t('cab_loading') : t('login_reset_submit')}
              </button>
            </form>
            <button className="login-link" type="button" onClick={() => setMode(SIGN_IN)}>{t('login_back')}</button>
          </>
        )}

        <Link className="login-home" to="/">← SkillUp Academy</Link>
      </div>
    </div>
  );
}
